import { FGSelectInput, FieldGroup } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { ERequestState } from "../../../../api";
import {
  FGDogSearchInput,
  FGPeopleSearchInput,
  FGSRSHCheckboxInput,
  FGSRSHDateMaskInput,
  FGSRSHMultiCheckboxInput,
  InformationTooltip,
} from "../../../../components";
import { useAuth, useGlobalData } from "../../../../contexts";
import { useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { DysplasiaRequestValueResult } from "../../../../pages/request/dysplasiaRequest/item/DysplasiaRequestValueResult";
import { DrupalStepperButtonBloc } from "../../../common";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";

export interface IDrupalDysplasiaRequestFormProps {
  dysplasiaRequestId: number;
  initialdata: any;
  loading: boolean;
}

const StyledEvaluation = styled.div`
  margin-left: 30px;
`;

export const DrupalDysplasiaRequestForm: React.FunctionComponent<IDrupalDysplasiaRequestFormProps> = ({
  loading,
  initialdata,
}) => {
  const { currentLanguage } = useGlobalData();
  const { t } = useTl();
  const { user } = useAuth();
  const [dysplasiaRequests, drloading] = useReferential(
    (a) =>
      +initialdata?.request?.requestId > 0 && initialdata?.dogId
        ? a.getOwnDysplasiaRequests(initialdata?.dogId)
        : a.getDysplasiaRequests(),
    true,
    [initialdata]
  );

  const allRadiosAccepted = React.useMemo(() => {
    let isAccepted = true;
    initialdata?.transientDysplasiaTypes?.forEach((v) => {
      if (v.isRefused || (!v.transientDysplasias[0].healthValueId && !v.transientDysplasias[0].numericValue)) {
        isAccepted = false;
      }
    });
    return isAccepted;
  }, [initialdata]);

  const [positions, posloading] = useReferential((a) => a.getDysplasiaPositions(currentLanguage.languageId), false);
  const [clinics, cloading] = useReferential((a) => a.getClinics(), true);
  const [commissionTypes, ctloading] = useReferential((a) => a.getCommissionTypes(), false);
  const [dysplasiaTypes, dtloading] = useReferential((a) => a.getDysplasiaTypes(true, 0), true, [initialdata]);
  const [dogs, dloading] = useReferential(
    (c) => c.getOwnedDogs(user.userId, initialdata?.dogId, null, null, null),
    true,
    [initialdata, user]
  );

  return (
    <DrupalFormGenerator
      initialValues={initialdata}
      editMode={false}
      onSubmit={() => {}}
      loading={loading}
      saving={false}
      deleting={false}
      hideButtons
    >
      <FieldGroup columns={2} fieldsetProps={{ divider: false, title: t(ETLCodes.RequestData) }}>
        <div>
          <FGSRSHDateMaskInput name="request.requestDate" label={t(ETLCodes.RequestDate)} />
          {initialdata?.request?.status === ERequestState.EXECUTED && (
            <FieldGroup columns={2}>
              <FGSRSHDateMaskInput name="request.executionDate" label={t(ETLCodes.ClosingDate)} />
            </FieldGroup>
          )}
          <FGSelectInput name="dogId" label={t(ETLCodes.Dog)} items={dogs} loading={dloading} />
        </div>
      </FieldGroup>
      <DrupalFieldSet title={t(ETLCodes.RequestData)}>
        <FieldGroup columns={2}>
          <FieldGroup columns={2}>
            <FGSRSHCheckboxInput name="isAppeal" label={t(ETLCodes.Appeal)} />
            <FGSelectInput
              name="appealRequestId"
              items={dysplasiaRequests}
              loading={drloading}
              visible={initialdata?.isAppeal}
            />
          </FieldGroup>
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGDogSearchInput name="dogId" label={t(ETLCodes.Dog)} />
        </FieldGroup>
        <FieldGroup columns={2}>
          {/* <FGPeopleSearchInput
            name="request.personId"
            label={t(ETLCodes.Contact)}
            info={
              <InformationTooltip
                tooltipMessage={ETLCodes.MustBeBelgianOwner}
              />
            }
            dogId={initialdata?.dogId}
          /> */}
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSelectInput
            name="commissionType"
            label={t(ETLCodes.CommissionType)}
            items={commissionTypes}
            loading={ctloading}
            visible={!initialdata?.isAppeal}
          />
        </FieldGroup>
      </DrupalFieldSet>
      <DrupalFieldSet title={t(ETLCodes.Radio)}>
        <FieldGroup columns={2}>
          <FGSRSHMultiCheckboxInput name="typeIdsChecked" items={dysplasiaTypes} loading={dtloading} columns={6} />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSRSHDateMaskInput name="radioDate" label={t(ETLCodes.RadioDate)} maxDate={new Date()} />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSelectInput name="position" label={t(ETLCodes.Position)} items={positions} loading={posloading} />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSelectInput name="clinicId" label={t(ETLCodes.Clinic)} items={clinics} loading={cloading} />
        </FieldGroup>
      </DrupalFieldSet>
      {allRadiosAccepted &&
        (initialdata?.request?.status === ERequestState.EVALUATED ||
          initialdata?.request?.status === ERequestState.EXECUTED) && (
          <DrupalFieldSet title={t(ETLCodes.Evaluations)}>
            {initialdata?.transientDysplasiaTypes?.map((v, index) => (
              <StyledEvaluation>
                <DrupalFieldSet title={`${t(ETLCodes.DysplasiaType)}: ${v.dysplasiaType}`}>
                  {v?.transientDysplasias?.map((d, dysplasiaIndex) => (
                    <div style={{ marginLeft: "3rem" }} key={dysplasiaIndex}>
                      <DrupalFieldSet title={d.characteristicName}>
                        <DysplasiaRequestValueResult
                          index={index}
                          characteristicId={d.characteristicId}
                          characteristicType={d.type}
                          dysplasiaIndex={dysplasiaIndex}
                        />
                      </DrupalFieldSet>
                    </div>
                  ))}
                </DrupalFieldSet>
              </StyledEvaluation>
            ))}
          </DrupalFieldSet>
        )}
      <DrupalStepperButtonBloc backOnly />
    </DrupalFormGenerator>
  );
};
